/**
 * Shared geometry helper functions for the bathroom analyzer pipeline
 */

// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const MATH_ABS = Math.abs;
const MATH_ATAN2 = Math.atan2;
const MATH_COS = Math.cos;
const MATH_MAX = Math.max;
const MATH_MIN = Math.min;
const MATH_SIN = Math.sin;
const MATH_SQRT = Math.sqrt;
// `Infinity` is a global too, and read once per call in the hot loops below.
const POSITIVE_INFINITY = Infinity;
const NEGATIVE_INFINITY = -Infinity;

/**
 * Calculate 4 corners of a rectangle given center, dimensions, and rotation angle
 *
 * **Handedness (issue #1127) — this is NOT the renderer's convention.** Local
 * `+X` maps to `(cos, +sin)` here, but to `(cos, -sin)` in Three.js, so for any
 * object whose `rotation.y` is not a multiple of 90° the footprint comes out
 * mirrored relative to what is drawn. Verified against `THREE.Object3D` at
 * 0/30/45/89.5/90/135/180°: passing `-rotation.y` reproduces the renderer
 * exactly at every angle; passing `+rotation.y` agrees only at multiples of 90°.
 *
 * The inconsistency does NOT cancel out when both operands use this function.
 * A global mirror would preserve overlap, but this re-rotates each rectangle
 * about its OWN centre while the centres stay put, so relative geometry really
 * changes: two 1.6 × 0.5 m rectangles at 30°, centres 1.0 m apart, report
 * `doPolygonsOverlap` **true** under `+ry` and **false** under `-ry`.
 *
 * Roughly 6% of dev objects are off-axis (32% of stairs), so this is live, not
 * theoretical. Nearly every call site in the repo passes `+rotation.y`; only
 * `shared-lib/collision/geometry.js` (clearance corners) and
 * `shared-lib/split-level/capWallsUnderStairs.js` pass the corrected
 * `-rotation.y`. Fixing one call site in isolation is therefore WORSE than
 * leaving it — it desynchronises that site from the SAT partners it is
 * compared against. Any fix has to move the whole convention at once; see
 * #1127 for the options and the verification plan.
 *
 * @param {Object} center - Center point {x, z}
 * @param {number} width - Width of rectangle
 * @param {number} depth - Depth of rectangle
 * @param {number} rotation - Rotation angle in radians
 * @returns {Object[]} Array of 4 corner points [{x, z}, ...]
 */
function calculateCorners(center, width, depth, rotation) {
  const hw = width / 2;
  const hd = depth / 2;

  const cos = MATH_COS(rotation);
  const sin = MATH_SIN(rotation);

  // Local corners (before rotation)
  // z- is back (toward wall), z+ is front (into room)
  const localCorners = [
    { x: -hw, z: -hd }, // back-left
    { x: hw, z: -hd }, // back-right
    { x: hw, z: hd }, // front-right
    { x: -hw, z: hd }, // front-left
  ];

  // Rotate and translate to world coordinates
  return localCorners.map((corner) => ({
    x: center.x + corner.x * cos - corner.z * sin,
    z: center.z + corner.x * sin + corner.z * cos,
  }));
}

/**
 * Calculate clearance corners where back edge aligns with physical back edge
 * and front edge extends further into the room
 *
 * @param {Object} center - Center of physical fixture {x, z}
 * @param {number} physicalDepth - Depth of physical fixture
 * @param {number} clearanceWidth - Width of clearance area
 * @param {number} clearanceDepth - Depth of clearance area
 * @param {number} rotation - Rotation angle in radians
 * @returns {Object[]} Array of 4 corner points [{x, z}, ...]
 */
function calculateClearanceCorners(
  center,
  physicalDepth,
  clearanceWidth,
  clearanceDepth,
  rotation
) {
  const hw = clearanceWidth / 2;
  const physicalHalfDepth = physicalDepth / 2;

  const cos = MATH_COS(rotation);
  const sin = MATH_SIN(rotation);

  // Back edge at same position as physical back edge: -physicalHalfDepth
  // Front edge extends to: -physicalHalfDepth + clearanceDepth
  const backZ = -physicalHalfDepth;
  const frontZ = -physicalHalfDepth + clearanceDepth;

  const localCorners = [
    { x: -hw, z: backZ }, // back-left
    { x: hw, z: backZ }, // back-right
    { x: hw, z: frontZ }, // front-right
    { x: -hw, z: frontZ }, // front-left
  ];

  // Rotate and translate to world coordinates
  return localCorners.map((corner) => ({
    x: center.x + corner.x * cos - corner.z * sin,
    z: center.z + corner.x * sin + corner.z * cos,
  }));
}

/**
 * Calculate rectangle corners given center, dimensions, and wall direction vectors
 * Alternative to calculateCorners when you have wall direction instead of rotation
 *
 * @param {Object} center - Center point {x, z}
 * @param {number} width - Width of rectangle (along wall)
 * @param {number} depth - Depth of rectangle (perpendicular to wall)
 * @param {Object} wallDir - Normalized wall direction vector {x, z}
 * @param {Object} wallPerp - Normalized perpendicular vector pointing into room {x, z}
 * @returns {Object[]} Array of 4 corner points [{x, z}, ...]
 */
function calculateRectCorners(center, width, depth, wallDir, wallPerp) {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;

  return [
    // back-left (against wall)
    {
      x: center.x - wallDir.x * halfWidth - wallPerp.x * halfDepth,
      z: center.z - wallDir.z * halfWidth - wallPerp.z * halfDepth,
    },
    // back-right (against wall)
    {
      x: center.x + wallDir.x * halfWidth - wallPerp.x * halfDepth,
      z: center.z + wallDir.z * halfWidth - wallPerp.z * halfDepth,
    },
    // front-right (into room)
    {
      x: center.x + wallDir.x * halfWidth + wallPerp.x * halfDepth,
      z: center.z + wallDir.z * halfWidth + wallPerp.z * halfDepth,
    },
    // front-left (into room)
    {
      x: center.x - wallDir.x * halfWidth + wallPerp.x * halfDepth,
      z: center.z - wallDir.z * halfWidth + wallPerp.z * halfDepth,
    },
  ];
}

/**
 * Get normalized wall direction vector
 * @param {Object} wall - Wall with start and end points
 * @returns {Object} Normalized direction vector {x, z}
 */
function getWallDirection(wall) {
  const dx = wall.end.x - wall.start.x;
  const dz = wall.end.z - wall.start.z;
  const length = MATH_SQRT(dx * dx + dz * dz);
  return { x: dx / length, z: dz / length };
}

/**
 * Get wall length
 * @param {Object} wall - Wall with start and end points
 * @returns {number} Wall length
 */
function getWallLength(wall) {
  const dx = wall.end.x - wall.start.x;
  const dz = wall.end.z - wall.start.z;
  return MATH_SQRT(dx * dx + dz * dz);
}

/**
 * Check if two convex polygons overlap using Separating Axis Theorem (SAT)
 * @param {Object[]} poly1 - First polygon corners [{x, z}, ...]
 * @param {Object[]} poly2 - Second polygon corners [{x, z}, ...]
 * @param {number} [tolerance=0] - Overlap tolerance in meters; overlaps
 *   smaller than this are ignored (treated as non-overlapping)
 * @returns {boolean} True if polygons overlap beyond tolerance
 */
function doPolygonsOverlap(poly1, poly2, tolerance = 0) {
  const edges = [...getEdges(poly1), ...getEdges(poly2)];

  for (const edge of edges) {
    const length = MATH_SQRT(edge.x * edge.x + edge.z * edge.z);
    const axis = { x: -edge.z / length, z: edge.x / length };

    const projection1 = projectPolygon(poly1, axis);
    const projection2 = projectPolygon(poly2, axis);

    if (
      projection1.max < projection2.min + tolerance ||
      projection2.max < projection1.min + tolerance
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Get edges of a polygon as direction vectors
 * @param {Object[]} polygon - Polygon corners [{x, z}, ...]
 * @returns {Object[]} Array of edge vectors [{x, z}, ...]
 */
function getEdges(polygon) {
  const edges = [];
  for (let i = 0; i < polygon.length; i++) {
    const next = (i + 1) % polygon.length;
    edges.push({
      x: polygon[next].x - polygon[i].x,
      z: polygon[next].z - polygon[i].z,
    });
  }
  return edges;
}

/**
 * Project polygon onto axis and return min/max
 * @param {Object[]} polygon - Polygon corners [{x, z}, ...]
 * @param {Object} axis - Axis vector {x, z}
 * @returns {Object} Projection range {min, max}
 */
function projectPolygon(polygon, axis) {
  let min = POSITIVE_INFINITY;
  let max = NEGATIVE_INFINITY;

  for (let i = 0; i < polygon.length; i++) {
    const point = polygon[i];
    const projection = point.x * axis.x + point.z * axis.z;
    min = MATH_MIN(min, projection);
    max = MATH_MAX(max, projection);
  }

  return { min, max };
}

// Point-in-polygon + segment distance live in the canonical shared module
// (issue #1030); re-exported below so this file stays the documented home
// for bathroom-pipeline callers.
const {
  isPointInsidePolygon,
  isPointInsideOrOnPolygon,
  distanceToLineSegment,
} = require('../../geometry/pointInPolygon');

/**
 * Deep clone a layout object
 * @param {Object} layout - Layout to clone
 * @returns {Object} Cloned layout
 */
function cloneLayout(layout) {
  return {
    ...layout,
    fixtures: layout.fixtures.map((f) => ({
      ...f,
      physicalCorners: f.physicalCorners.map((c) => ({ ...c })),
      clearanceCorners: f.clearanceCorners.map((c) => ({ ...c })),
      ...(Array.isArray(f.clearanceFootprints) && {
        clearanceFootprints: f.clearanceFootprints.map((fp) =>
          fp.map((c) => ({ ...c }))
        ),
      }),
    })),
  };
}

// ============================================================================
// CORNERS-ONLY HELPER FUNCTIONS
// These functions derive values from corners, which are the source of truth
// ============================================================================

/**
 * Derive center point from corners (average of diagonal corners)
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @returns {Object} Center point {x, z}
 */
function getFixtureCenter(corners) {
  if (!corners || corners.length < 4 || !corners[0] || !corners[2]) {
    return { x: 0, z: 0 };
  }
  return {
    x: (corners[0].x + corners[2].x) / 2,
    z: (corners[0].z + corners[2].z) / 2,
  };
}

/**
 * Derive width from corners (distance along back edge: corners[0] to corners[1])
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @returns {number} Width of the fixture
 */
function getFixtureWidth(corners) {
  if (!corners || corners.length < 2 || !corners[0] || !corners[1]) {
    return 0;
  }
  const dx = corners[1].x - corners[0].x;
  const dz = corners[1].z - corners[0].z;
  return MATH_SQRT(dx * dx + dz * dz);
}

/**
 * Derive depth from corners (distance from back to front: corners[1] to corners[2])
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @returns {number} Depth of the fixture
 */
function getFixtureDepth(corners) {
  if (!corners || corners.length < 3 || !corners[1] || !corners[2]) {
    return 0;
  }
  const dx = corners[2].x - corners[1].x;
  const dz = corners[2].z - corners[1].z;
  return MATH_SQRT(dx * dx + dz * dz);
}

/**
 * Derive rotation from corners (back edge direction: corners[0] → corners[1])
 * This returns the angle of the back edge (parallel to wall)
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @returns {number} Rotation angle in radians
 */
function getFixtureRotation(corners) {
  if (!corners || corners.length < 2 || !corners[0] || !corners[1]) {
    return 0;
  }
  // Back edge direction (corners[0] -> corners[1])
  // Negated to match 3D rendering convention (2D placement uses opposite rotation sign)
  const dx = corners[1].x - corners[0].x;
  const dz = corners[1].z - corners[0].z;
  return -MATH_ATAN2(dz, dx);
}

/**
 * Derive position along wall from corners and wall
 * Returns distance from wall start to back-left corner projected onto wall
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @param {Object} wall - Wall with start point {x, z}
 * @returns {number} Position along wall in meters
 */
function getPositionAlongWall(corners, wall) {
  if (!corners || !corners[0] || !wall || !wall.start) {
    return 0;
  }
  const backLeft = corners[0];
  const wallDir = getWallDirection(wall);
  // Project back-left corner onto wall direction from wall start
  const dx = backLeft.x - wall.start.x;
  const dz = backLeft.z - wall.start.z;
  // Get center position (back-left is at position - halfWidth)
  const width = getFixtureWidth(corners);
  return dx * wallDir.x + dz * wallDir.z + width / 2;
}

/**
 * Shift all corners by a given amount
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @param {number} dx - Amount to shift in x direction
 * @param {number} dz - Amount to shift in z direction
 * @returns {Object[]} New array of shifted corner points
 */
function shiftCorners(corners, dx, dz) {
  if (!corners || !Array.isArray(corners)) {
    return [];
  }
  return corners.map((c) => ({ x: c.x + dx, z: c.z + dz }));
}

/**
 * Shift corners along the wall direction by a given amount
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @param {number} amount - Amount to shift along wall (positive = right, negative = left)
 * @param {Object} wallDir - Normalized wall direction vector {x, z}
 * @returns {Object[]} New array of shifted corner points
 */
function shiftCornersAlongWall(corners, amount, wallDir) {
  return shiftCorners(corners, wallDir.x * amount, wallDir.z * amount);
}

/**
 * Expand width symmetrically (shift corners 0,3 left and corners 1,2 right)
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @param {number} amount - Total amount to expand width by
 * @param {Object} wallDir - Normalized wall direction vector {x, z}
 * @returns {Object[]} New array of expanded corner points
 */
function expandWidth(corners, amount, wallDir) {
  const halfAmount = amount / 2;
  return [
    {
      x: corners[0].x - wallDir.x * halfAmount,
      z: corners[0].z - wallDir.z * halfAmount,
    },
    {
      x: corners[1].x + wallDir.x * halfAmount,
      z: corners[1].z + wallDir.z * halfAmount,
    },
    {
      x: corners[2].x + wallDir.x * halfAmount,
      z: corners[2].z + wallDir.z * halfAmount,
    },
    {
      x: corners[3].x - wallDir.x * halfAmount,
      z: corners[3].z - wallDir.z * halfAmount,
    },
  ];
}

/**
 * Expand width asymmetrically in one direction
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @param {number} amount - Amount to expand
 * @param {Object} wallDir - Normalized wall direction vector {x, z}
 * @param {string} direction - 'left' or 'right'
 * @returns {Object[]} New array of expanded corner points
 */
function expandWidthAsymmetric(corners, amount, wallDir, direction) {
  if (direction === 'left') {
    // Expand left side (corners 0 and 3)
    return [
      {
        x: corners[0].x - wallDir.x * amount,
        z: corners[0].z - wallDir.z * amount,
      },
      { ...corners[1] },
      { ...corners[2] },
      {
        x: corners[3].x - wallDir.x * amount,
        z: corners[3].z - wallDir.z * amount,
      },
    ];
  } else {
    // Expand right side (corners 1 and 2)
    return [
      { ...corners[0] },
      {
        x: corners[1].x + wallDir.x * amount,
        z: corners[1].z + wallDir.z * amount,
      },
      {
        x: corners[2].x + wallDir.x * amount,
        z: corners[2].z + wallDir.z * amount,
      },
      { ...corners[3] },
    ];
  }
}

/**
 * Expand depth (shift front corners 2,3 forward into room)
 * @param {Object[]} corners - Array of 4 corner points [{x, z}, ...]
 * @param {number} amount - Amount to expand depth by
 * @param {Object} wallNormal - Normalized wall normal vector pointing into room {x, z}
 * @returns {Object[]} New array of expanded corner points
 */
function expandDepth(corners, amount, wallNormal) {
  return [
    { ...corners[0] }, // back-left stays
    { ...corners[1] }, // back-right stays
    {
      x: corners[2].x + wallNormal.x * amount,
      z: corners[2].z + wallNormal.z * amount,
    }, // front-right moves
    {
      x: corners[3].x + wallNormal.x * amount,
      z: corners[3].z + wallNormal.z * amount,
    }, // front-left moves
  ];
}

/**
 * Get window zone polygon (thin rectangle at window position)
 * @param {Object} window - Window data with parentWallIds, width, center
 * @param {Object} bathroom - Bathroom data with walls
 * @returns {Object[]|null} Window zone corners or null
 */
function getWindowZone(window, bathroom) {
  const wall = bathroom.walls.find((w) => w.id === window.parentWallIds?.[0]);
  if (!wall) return null;

  const wallDir = getWallDirection(wall);
  // Compute wall normal if not provided (perpendicular to wall direction, pointing into room)
  const wallNormal = wall.normal || { x: -wallDir.z, z: wallDir.x };

  const windowWidth = window.width || 1.0;
  const windowDepth = 0.25; // Zone depth
  const offsetIntoRoom = 0.225; // Center offset so back edge is 0.1m from wall

  const windowCenter = {
    x: window.center?.x ?? (wall.start.x + wall.end.x) / 2,
    z: window.center?.z ?? (wall.start.z + wall.end.z) / 2,
  };

  // Offset center into room using wall normal
  const offsetCenter = {
    x: windowCenter.x + wallNormal.x * offsetIntoRoom,
    z: windowCenter.z + wallNormal.z * offsetIntoRoom,
  };

  return calculateRectCorners(
    offsetCenter,
    windowWidth,
    windowDepth,
    wallDir,
    wallNormal
  );
}

/**
 * Window overlap states
 */
const WINDOW_OVERLAP_STATE = {
  NONE: 'none', // No overlap with any window
  CONTAINED: 'contained', // Fixture fully contains window zone
  PARTIAL: 'partial', // Partial overlap (window not fully contained)
};

/**
 * Get the overlap state between a fixture and windows
 * @param {Object} fixture - Fixture with physicalCorners
 * @param {Object} bathroom - Bathroom data with windows and walls
 * @returns {string} One of WINDOW_OVERLAP_STATE values
 */
function getFixtureWindowOverlapState(fixture, bathroom) {
  if (!bathroom.windows || bathroom.windows.length === 0) {
    return WINDOW_OVERLAP_STATE.NONE;
  }

  let hasPartialOverlap = false;

  for (const window of bathroom.windows) {
    const windowZone = getWindowZone(window, bathroom);
    if (!windowZone) continue;

    const overlaps = doPolygonsOverlap(fixture.physicalCorners, windowZone);
    if (!overlaps) continue;

    // Check if fixture contains the window zone's front corners (into room)
    // Back corners (indices 0,1) are inside the wall and no fixture footprint reaches there
    // Front-right = index 2, front-left = index 3
    const wall = bathroom.walls.find((w) => w.id === window.parentWallIds?.[0]);
    let frontCorners = [windowZone[2], windowZone[3]];
    if (wall && fixture.physicalCorners?.length >= 4) {
      const wallDir = getWallDirection(wall);
      const wallStartProj = wall.start.x * wallDir.x + wall.start.z * wallDir.z;
      const wallEndProj = wall.end.x * wallDir.x + wall.end.z * wallDir.z;
      const wallMinProj = MATH_MIN(wallStartProj, wallEndProj);
      const wallMaxProj = MATH_MAX(wallStartProj, wallEndProj);
      const fixtureProjs = fixture.physicalCorners.map(
        (c) => c.x * wallDir.x + c.z * wallDir.z
      );
      const fixtureMin = MATH_MIN(...fixtureProjs);
      const fixtureMax = MATH_MAX(...fixtureProjs);
      const CORNER_TOLERANCE = 0.05; // 5cm
      const atLeftCorner = fixtureMin - wallMinProj <= CORNER_TOLERANCE;
      const atRightCorner = wallMaxProj - fixtureMax <= CORNER_TOLERANCE;
      // Skip front corner checks on sides where shower reaches the wall corner
      frontCorners = frontCorners.filter((_, i) => {
        if (i === 0 && atRightCorner) return false; // front-right
        if (i === 1 && atLeftCorner) return false; // front-left
        return true;
      });
    }
    const fullyContains =
      frontCorners.length === 0 ||
      frontCorners.every((corner) =>
        isPointInsideOrOnPolygon(corner, fixture.physicalCorners, 0.001)
      );

    if (fullyContains) {
      return WINDOW_OVERLAP_STATE.CONTAINED;
    } else {
      hasPartialOverlap = true;
    }
  }

  return hasPartialOverlap
    ? WINDOW_OVERLAP_STATE.PARTIAL
    : WINDOW_OVERLAP_STATE.NONE;
}

/**
 * Check if a fixture's physical body overlaps any window on its wall
 * @param {Object} fixture - Fixture with physicalCorners and wallId
 * @param {Object} bathroom - Bathroom data with windows and walls
 * @returns {boolean} True if fixture overlaps a window
 */
function doesFixtureOverlapWindow(fixture, bathroom) {
  const state = getFixtureWindowOverlapState(fixture, bathroom);
  return state !== WINDOW_OVERLAP_STATE.NONE;
}

/**
 * Check if a fixture completely contains a window zone
 * @param {Object} fixture - Fixture with physicalCorners
 * @param {Object} bathroom - Bathroom data with windows and walls
 * @returns {boolean} True if fixture completely contains any window zone
 */
function doesFixtureContainWindow(fixture, bathroom) {
  return (
    getFixtureWindowOverlapState(fixture, bathroom) ===
    WINDOW_OVERLAP_STATE.CONTAINED
  );
}

// ============================================================================
// ALCOVE DETECTION (for tub-shower combos)
// ============================================================================

// A tub-shower combo requires a recess flanked by two walls that are
// perpendicular to its back wall, parallel to each other, both extending into
// the room, and long enough to host an alcove tub.
//
// The BAND is derived from two decisions already made elsewhere rather than
// picked here (#1865 step 2). Its floor is the narrowest alcove tub the
// catalog stocks — below that nothing can be built. Its ceiling is the widest
// (66") plus the filler allowance `BOOKEND_MAX_GAP_M` already rules on in
// `shared-lib/tub-surround/bookend.js`; a recess wider than a tub is bridged
// by a bookend, and "how much filler is still a filler" is the same question
// asked there.
//
// It used to be 60" ± 2 cm, which fired on 15 of dev's 520 bathrooms (2.9%)
// while the scan path inferred an alcove tub in 19 — and caught only 3 of
// those 19. The band below catches all 19. Measured with
// `scripts/measure-alcove-gate.js`; widening the ceiling past 72" adds 39
// more rooms and NO recall, so the floor is what was costing us.
const MIN_ALCOVE_TUB_WIDTH = 1.1811; // 46.5" — narrowest catalog alcove tub
const MAX_ALCOVE_TUB_WIDTH = 1.6764; // 66" — widest catalog alcove tub
const ALCOVE_FILLER_ALLOWANCE = 0.1524; // 6" — recall saturates here
const MIN_ALCOVE_RECESS = MIN_ALCOVE_TUB_WIDTH;
const MAX_ALCOVE_RECESS = MAX_ALCOVE_TUB_WIDTH + ALCOVE_FILLER_ALLOWANCE;
// ~3°: 1.15° rejected walls whose sides came out of wall processing a
// fraction off square, which is most of them on a real scan.
const ALCOVE_ANGLE_TOLERANCE = 0.0523;
const MIN_ALCOVE_SIDE_DEPTH = 0.711; // 28" — smallest real alcove-tub depth

function pointsApproxEqual(a, b, eps = 0.01) {
  return MATH_ABS(a.x - b.x) <= eps && MATH_ABS(a.z - b.z) <= eps;
}

/**
 * Return neighbor wall that shares a given endpoint with `wall`, or null.
 */
function findNeighborAtPoint(wall, point, walls) {
  for (const other of walls) {
    if (other.id === wall.id) continue;
    if (
      pointsApproxEqual(other.start, point) ||
      pointsApproxEqual(other.end, point)
    ) {
      return other;
    }
  }
  return null;
}

/**
 * Unit direction vector from `sharedPoint` toward the neighbor's far endpoint.
 */
function directionAwayFrom(neighbor, sharedPoint) {
  const atStart = pointsApproxEqual(neighbor.start, sharedPoint);
  const from = atStart ? neighbor.start : neighbor.end;
  const to = atStart ? neighbor.end : neighbor.start;
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len = MATH_SQRT(dx * dx + dz * dz);
  return { x: dx / len, z: dz / len };
}

/**
 * Check if a wall qualifies as the back wall of a 60" alcove suitable for a
 * tub-shower combo.
 *
 * Conditions:
 *  1. Wall length within the alcove band — a recess an alcove tub fits, with
 *     at most `ALCOVE_FILLER_ALLOWANCE` of filler bridging the remainder.
 *  2. Both neighboring walls (sharing endpoints with `wall`) are perpendicular
 *     to it.
 *  3. Both neighbors are parallel to each other and point the same direction
 *     away from the back wall (forming a U-shaped alcove).
 *  4. That shared direction matches the back wall's inward normal (alcove
 *     opens into the room, not out of it).
 *  5. Both neighbors extend at least MIN_ALCOVE_SIDE_DEPTH (28") inward so a
 *     real alcove tub can fit. Per-model fit is enforced later at tub-model
 *     selection, not here.
 *
 * @param {Object} wall - Candidate back wall
 * @param {Object[]} walls - All walls of the bathroom
 * @returns {boolean}
 */
function isAlcoveWall(wall, walls) {
  return getAlcoveSideWalls(wall, walls) !== null;
}

/**
 * If `wall` is the back wall of a 60" alcove, return its two perpendicular
 * parallel side walls (as an array [startNeighbor, endNeighbor]). Otherwise
 * return null. Implements the same conditions as isAlcoveWall.
 *
 * @param {Object} wall - Candidate back wall
 * @param {Object[]} walls - All walls
 * @returns {Object[]|null}
 */
function getAlcoveSideWalls(wall, walls) {
  const length = wall.length ?? getWallLength(wall);
  if (length < MIN_ALCOVE_RECESS || length > MAX_ALCOVE_RECESS) {
    return null;
  }

  const neighborAtStart = findNeighborAtPoint(wall, wall.start, walls);
  const neighborAtEnd = findNeighborAtPoint(wall, wall.end, walls);
  if (!neighborAtStart || !neighborAtEnd) return null;

  const wallDir = getWallDirection(wall);
  const startDir = directionAwayFrom(neighborAtStart, wall.start);
  const endDir = directionAwayFrom(neighborAtEnd, wall.end);

  if (
    MATH_ABS(startDir.x * wallDir.x + startDir.z * wallDir.z) >
    ALCOVE_ANGLE_TOLERANCE
  ) {
    return null;
  }
  if (
    MATH_ABS(endDir.x * wallDir.x + endDir.z * wallDir.z) >
    ALCOVE_ANGLE_TOLERANCE
  ) {
    return null;
  }

  const neighborDot = startDir.x * endDir.x + startDir.z * endDir.z;
  if (neighborDot < 1 - ALCOVE_ANGLE_TOLERANCE) return null;

  const normalDot = startDir.x * wall.normal.x + startDir.z * wall.normal.z;
  if (normalDot < 1 - ALCOVE_ANGLE_TOLERANCE) return null;

  const startLen = neighborAtStart.length ?? getWallLength(neighborAtStart);
  const endLen = neighborAtEnd.length ?? getWallLength(neighborAtEnd);
  if (startLen < MIN_ALCOVE_SIDE_DEPTH) return null;
  if (endLen < MIN_ALCOVE_SIDE_DEPTH) return null;

  return [neighborAtStart, neighborAtEnd];
}

/**
 * Convert a wall to an outward-extruded oriented bounding box (OBB) polygon.
 *
 * The wall's inside face (along wall.start → wall.end) sits exactly on the
 * room boundary. The OBB extends outward (in the -wall.normal direction,
 * away from the room) by `thickness`. Inside corners and outside corners
 * form a 4-corner polygon usable as a SAT obstacle.
 *
 * Adjacent walls' OBBs share the inside corner where their inside faces meet
 * but extrude in different outward directions, so they touch but do not
 * overlap — a fixture flush against the inside corner has zero penetration
 * into both wall OBBs.
 *
 * @param {Object} wall - Wall with start, end, normal (normal points INTO the room)
 * @param {number} [thickness=0.05] - OBB thickness outward from the room (meters)
 * @returns {Object[]} 4-corner polygon [{x,z}, ...] in order:
 *   [insideStart, insideEnd, outsideEnd, outsideStart] — counter-clockwise
 *   when viewed from above (matching room boundary convention)
 */
function wallToObb(wall, thickness = 0.05) {
  // Outward direction = -wall.normal (wall.normal points into room)
  const outX = -wall.normal.x * thickness;
  const outZ = -wall.normal.z * thickness;

  return [
    { x: wall.start.x, z: wall.start.z }, // inside start (on boundary)
    { x: wall.end.x, z: wall.end.z }, // inside end (on boundary)
    { x: wall.end.x + outX, z: wall.end.z + outZ }, // outside end
    { x: wall.start.x + outX, z: wall.start.z + outZ }, // outside start
  ];
}

/**
 * Return a fixture's clearance polygons as an array.
 *
 * Multi-clearance fixtures (e.g. `double-sink` after stage 7) carry
 * `clearanceFootprints: Polygon[]`. Single-clearance fixtures only have
 * `clearanceCorners`. Callers that need per-footprint fidelity iterate the
 * return value; coarse callers can keep using `clearanceCorners` directly
 * (it stays the union AABB).
 */
function getFixtureClearanceFootprints(fixture) {
  return fixture.clearanceFootprints || [fixture.clearanceCorners];
}

/**
 * A corner shower is a `shower` fixture attached to two perpendicular walls
 * (rectangular corner shower) — diagonal corner showers (`doorType: 'diagonal'`)
 * are a sub-case. Sliding such a shower along one of its parent walls would
 * detach it from the corner, so push paths in S5/S6 must skip it.
 */
function isCornerShower(fixture) {
  return (
    fixture?.type === 'shower' && (fixture.parentWallIds?.length ?? 0) >= 2
  );
}

module.exports = {
  calculateCorners,
  calculateClearanceCorners,
  calculateRectCorners,
  getWallDirection,
  getWallLength,
  doPolygonsOverlap,
  getEdges,
  projectPolygon,
  isPointInsidePolygon,
  isPointInsideOrOnPolygon,
  distanceToLineSegment,
  cloneLayout,
  getFixtureCenter,
  getFixtureWidth,
  getFixtureDepth,
  getFixtureRotation,
  getPositionAlongWall,
  getFixtureClearanceFootprints,
  shiftCorners,
  shiftCornersAlongWall,
  expandWidth,
  expandWidthAsymmetric,
  expandDepth,
  getWindowZone,
  getFixtureWindowOverlapState,
  doesFixtureOverlapWindow,
  doesFixtureContainWindow,
  isAlcoveWall,
  getAlcoveSideWalls,
  MIN_ALCOVE_RECESS,
  MAX_ALCOVE_RECESS,
  isCornerShower,
  wallToObb,
  WINDOW_OVERLAP_STATE,
};
